import { useState, useEffect, useCallback } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type { InvitationPolicyDTO } from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useInvitationPolicy() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<InvitationPolicyDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requireInviteCode, setRequireInviteCode] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getInvitationPolicy();
      setData(result);
      setRequireInviteCode(result.require_invite_code);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await settingsService.updateInvitationPolicy(requireInviteCode);
      await load();
      addToast('success', '邀请码策略已保存');
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSaving(false);
    }
  }, [requireInviteCode, addToast, load]);

  return { data, loading, saving, error, requireInviteCode, setRequireInviteCode, save, reload: load };
}
